import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../../../services/baseQuery';
import { extractErrorMessage } from '../../../utils/errorUtils';
import type { ApiResponse } from '../../menu/types';
import { toCamelCase } from '../../grid/utils';
import type {
  FinanceEmailInboxThreadDetail,
  FinanceEmailInboxThreadListItem,
  FinanceEmailInboxThreadListResult,
} from '../types';

export const FINANCE_EMAIL_INBOX_API = '/umbraco/api/FinanceEmailInbox';
export const FINANCE_EMAIL_INBOX_PAGE_SIZE = 40;

const unwrap = <T>(response: ApiResponse<T>, fallback: string): T => {
  if ((response.Code ?? response.code) === 1) {
    return toCamelCase(response.data) as T;
  }

  throw new Error(extractErrorMessage(response, fallback));
};

type MarkReadStateResponse = {
  message?: string;
};

type ThreadsQueryArg = {
  search?: string;
  unreadOnly?: boolean;
};

const parseMarkResponse = (response: ApiResponse<MarkReadStateResponse>, fallback: string) => {
  if ((response.Code ?? response.code) === 1) {
    const data = response.data as MarkReadStateResponse | string | undefined;
    if (typeof data === 'string') return data;
    return data?.message;
  }

  throw new Error(extractErrorMessage(response, fallback));
};

export const financeEmailInboxApi = createApi({
  reducerPath: 'financeEmailInboxApi',
  baseQuery,
  tagTypes: ['FinanceEmailInbox'],
  endpoints: (builder) => ({
    getFinanceEmailInboxThreads: builder.infiniteQuery<
      FinanceEmailInboxThreadListResult,
      ThreadsQueryArg,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (lastPage, _allPages, lastPageParam) =>
          lastPage.hasMore ? lastPageParam + lastPage.items.length : undefined,
      },
      query: ({ queryArg, pageParam }) => ({
        url: `${FINANCE_EMAIL_INBOX_API}/Threads`,
        method: 'GET',
        params: {
          search: queryArg.search?.trim() || undefined,
          unreadOnly: Boolean(queryArg.unreadOnly),
          skip: pageParam,
          take: FINANCE_EMAIL_INBOX_PAGE_SIZE,
        },
      }),
      transformResponse: (response: ApiResponse<FinanceEmailInboxThreadListResult>) => {
        const data = unwrap(response, 'Failed to load inbox threads.');
        return {
          items: Array.isArray(data.items) ? data.items : [],
          totalCount: data.totalCount ?? 0,
          unreadCount: data.unreadCount ?? 0,
          hasMore: Boolean(data.hasMore),
        };
      },
      providesTags: [{ type: 'FinanceEmailInbox', id: 'LIST' }],
    }),
    getFinanceEmailInboxThread: builder.query<FinanceEmailInboxThreadDetail, string>({
      query: (threadId) => ({
        url: `${FINANCE_EMAIL_INBOX_API}/Thread`,
        method: 'GET',
        params: { threadId },
      }),
      transformResponse: (response: ApiResponse<FinanceEmailInboxThreadDetail>) =>
        unwrap(response, 'Failed to load thread.'),
      providesTags: (_result, _error, threadId) => [{ type: 'FinanceEmailInbox', id: threadId }],
    }),
    markFinanceEmailInboxRead: builder.mutation<
      string | undefined,
      { historyIds: string[]; threadId?: string }
    >({
      query: ({ historyIds }) => ({
        url: `${FINANCE_EMAIL_INBOX_API}/MarkRead`,
        method: 'POST',
        body: { historyIds },
      }),
      transformResponse: (response: ApiResponse<MarkReadStateResponse>) =>
        parseMarkResponse(response, 'Failed to mark emails as read.'),
      async onQueryStarted({ historyIds, threadId }, { dispatch, queryFulfilled }) {
        const idSet = new Set(historyIds.map((id) => id.toLowerCase()));
        const threadPatch = threadId
          ? dispatch(
              financeEmailInboxApi.util.updateQueryData(
                'getFinanceEmailInboxThread',
                threadId,
                (draft) => {
                  for (const message of draft.messages) {
                    if (idSet.has(message.historyId.toLowerCase())) {
                      message.isHandled = true;
                    }
                  }
                }
              )
            )
          : null;

        try {
          await queryFulfilled;
          dispatch(
            financeEmailInboxApi.util.invalidateTags([{ type: 'FinanceEmailInbox', id: 'LIST' }])
          );
        } catch {
          threadPatch?.undo();
        }
      },
    }),
    markFinanceEmailInboxUnread: builder.mutation<
      string | undefined,
      { historyIds: string[]; threadId?: string }
    >({
      query: ({ historyIds }) => ({
        url: `${FINANCE_EMAIL_INBOX_API}/MarkUnread`,
        method: 'POST',
        body: { historyIds },
      }),
      transformResponse: (response: ApiResponse<MarkReadStateResponse>) =>
        parseMarkResponse(response, 'Failed to mark emails as unread.'),
      async onQueryStarted({ historyIds, threadId }, { dispatch, queryFulfilled }) {
        const idSet = new Set(historyIds.map((id) => id.toLowerCase()));
        const threadPatch = threadId
          ? dispatch(
              financeEmailInboxApi.util.updateQueryData(
                'getFinanceEmailInboxThread',
                threadId,
                (draft) => {
                  for (const message of draft.messages) {
                    if (idSet.has(message.historyId.toLowerCase())) {
                      message.isHandled = false;
                    }
                  }
                }
              )
            )
          : null;

        try {
          await queryFulfilled;
          dispatch(
            financeEmailInboxApi.util.invalidateTags([{ type: 'FinanceEmailInbox', id: 'LIST' }])
          );
        } catch {
          threadPatch?.undo();
        }
      },
    }),
  }),
});

export const {
  useGetFinanceEmailInboxThreadsInfiniteQuery,
  useGetFinanceEmailInboxThreadQuery,
  useLazyGetFinanceEmailInboxThreadQuery,
  useMarkFinanceEmailInboxReadMutation,
  useMarkFinanceEmailInboxUnreadMutation,
} = financeEmailInboxApi;

export type { FinanceEmailInboxThreadListItem };
